import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Skeleton } from 'primereact/skeleton';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { GetMyCart, CartDelete, CartDeleteAll } from "../../api/cartService";
import { insertEnquiry } from "../../api/enquiryService";
import { useAuth } from "../../context/AuthContext";
import { useCount } from "../../context/CountContext";
import { useLoader } from "../../context/LoaderContext";
import "../../css/VaultAccess/live_cart.css";
import CustomerSelection from "../../components/Common/CustomerSelection";
import EnquiryModal from "../Enquiry/EnquiryModal";
import CommonPopUpModal from "../../components/Common/CommonPopUpModal";

const DEFAULT_IMAGE = "/assets/images/VaultAccess/no-image3.png";

export default function MyCart() {
    document.title = "Cart | Meera Jewellery House Admin";
    const { user, showToast } = useAuth();
    const {
        fetchCartCount,
        enquiryItems,
        setEnquiryItems,
        enquiryRemark,
        setEnquiryRemark,
        isEnquiryOpen,
        setIsEnquiryOpen,
        submittingEnquiry,
        submitEnquiry: submitEnquiryGlobal,
        isSuccessModalOpen,
        setIsSuccessModalOpen,
        successTitle,
        successMessage,
        successType
    } = useCount();
    const { showLoader, hideLoader } = useLoader();
    const [cartItems, setCartItems] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const groupedData = useMemo(() => {
        const groups = {};
        cartItems.forEach(item => {
            const customerName = item.CustomerName || "Unknown Customer";
            if (!groups[customerName]) {
                groups[customerName] = [];
            }
            groups[customerName].push(item);
        });
        return groups;
    }, [cartItems]);
    const fetchCartData = async () => {
        setIsLoading(true);
        try {
            const payload = {
                Customer_ID: user?.SelectedCustomer_ID || "0"
            };
            const data = await GetMyCart(payload);
            setCartItems(data?.CartItems || data?.ROW || []);
        } catch (error) {
            console.error("Failed to fetch cart:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchCartData();
    }, [user?.SelectedCustomer_ID]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(cartItems.map(item => item.Cart_ID));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectItem = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const openDeleteModal = (item) => {
        setItemToDelete(item);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;

        if (!user?.SelectedCustomer_ID || user?.SelectedCustomer_ID === 0) {
            showToast({
                title: "Selection Required",
                message: "Please select a specific customer to remove items.",
                type: "warning"
            });
            return;
        }

        try {
            const res = await CartDelete({
                Party_ID: user?.SelectedCustomer_ID,
                Style_ID: itemToDelete.Style_ID,
                Cart_ID: itemToDelete.Cart_ID?.toString()
            });
            if (res?.Message?.[0]?.RETURNCODE === "1" || res?.ZMESSAGE?.[0]?.RETURNCODE === "1") {
                setCartItems(prev => prev.filter(item => item.Cart_ID !== itemToDelete.Cart_ID));
                fetchCartCount(user?.SelectedCustomer_ID);
                fetchCartData();
                setShowDeleteModal(false);
                setItemToDelete(null);
                showToast({
                    title: "Removed",
                    message: "Item removed from cart successfully.",
                    type: "success"
                });
            }
        } catch (error) {
            console.error("Delete failed:", error);
        }
    };

    const openBulkDeleteModal = () => {
        if (selectedIds.length > 1) {
            setShowBulkDeleteModal(true);
        }
    };

    const confirmBulkDelete = async () => {
        if (!user?.SelectedCustomer_ID || user?.SelectedCustomer_ID === 0) {
            showToast({
                title: "Selection Required",
                message: "Please select a specific customer to remove items.",
                type: "warning"
            });
            return;
        }

        try {
            const selectedItems = cartItems.filter(item => selectedIds.includes(item.Cart_ID));
            const items = selectedItems.map(item => ({
                Cart_ID: item.Cart_ID?.toString(),
                Style_ID: item.Style_ID?.toString()
            }));

            const res = await CartDeleteAll({
                Party_ID: user?.SelectedCustomer_ID,
                Items: items
            });
            
            if (res?.Message?.[0]?.RETURNCODE === "1" || res?.ZMESSAGE?.[0]?.RETURNCODE === "1") {
                fetchCartCount(user?.SelectedCustomer_ID);
                fetchCartData();
                setShowBulkDeleteModal(false);
                setSelectedIds([]);
                showToast({
                    title: "Removed",
                    message: "Selected items removed from cart successfully.",
                    type: "success"
                });
            }
        } catch (error) {
            console.error("Bulk delete failed:", error);
        }
    };

    const formatPrice = (price) => {
        if (typeof price === "string") {
            price = parseFloat(price.replace(/[^0-9.]/g, ""));
        }
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(price || 0).replace('₹', 'Rs. ');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        }) + " " + date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const deleteModalFooter = (
        <div className="cart-modal-footer">
            <button
                onClick={() => setShowDeleteModal(false)}
                className="cart-modal-btn-secondary"
            >
                Keep it
            </button>
            <button
                onClick={confirmDelete}
                className="cart-modal-btn-danger"
            >
                <i className="pi pi-trash" style={{ fontSize: '0.9rem' }} />
                Remove Item
            </button>
        </div>
    );

    const bulkDeleteModalFooter = (
        <div className="cart-modal-footer">
            <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="cart-modal-btn-secondary"
            >
                Cancel
            </button>
            <button
                onClick={confirmBulkDelete}
                className="cart-modal-btn-danger"
            >
                <i className="pi pi-trash" style={{ fontSize: '0.9rem' }} />
                Remove All ({selectedIds.length})
            </button>
        </div>
    );

    const calculateTotal = () => {
        const selectedItems = cartItems.filter(item => selectedIds.includes(item.Cart_ID));
        return selectedItems.reduce((acc, item) => {
            const price = parseFloat((item.LineAmount || item.TotalAmount || "0").replace(/[^0-9.]/g, ""));
            return acc + (isNaN(price) ? 0 : price);
        }, 0);
    };

    const resetEnquiryState = () => {
        setIsEnquiryOpen(false);
    };

    const removeEnquiryItem = (productId) => {
        setEnquiryItems(prev => prev.filter(p => p.Style_ID !== productId));
    };

    const handleProceedToEnquiry = async () => {
        const selectedItems = cartItems.filter(item => selectedIds.includes(item.Cart_ID));
        if (selectedItems.length === 0) return;

        const enquiryItemsFormatted = selectedItems.map(item => ({
            Style_ID: item.Style_ID,
            StyleNo: item.StyleNo,
            JewelCode: item.JewelCode,
            Shortdescription: item.Line1 || item.StyleNo,
            MetalQuality: item.MetalQuality,
            MetalTone: item.MetalTone,
            TotalNetWt: item.TotalNetWt ? `${item.TotalNetWt} g` : "-",
            CUSTOMERNAME: item.CustomerName || item.CUSTOMERNAME,
            Cart_ID: item.Cart_ID?.toString()
        }));

        setEnquiryItems(enquiryItemsFormatted);
        setIsEnquiryOpen(true);
    };

    const submitEnquiry = async () => {
        const res = await submitEnquiryGlobal(user?.SelectedCustomer_ID);
        if (res.success) {
            showToast({
                title: "Enquiry Submitted",
                message: "Your enquiry has been successfully submitted.",
                type: "success",
            });
            await fetchCartCount(user?.SelectedCustomer_ID);
            await fetchCartData();
            setSelectedIds([]);
        } else {
            showToast({
                title: "Submission Error",
                message: res.message || "Failed to submit enquiry.",
                type: "error",
            });
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        show: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }
        },
        exit: {
            opacity: 0,
            y: 20,
            transition: { duration: 0.2 }
        }
    };

    const renderCartItem = (item, index) => (
        <motion.div
            key={item.Cart_ID || index}
            initial="hidden"
            animate="show"
            exit="exit"
            variants={itemVariants}
            className="live-cart-item vault-access-cart"
        >
            <div className="item-left-visual">
                <input
                    type="checkbox"
                    className="item-img-checkbox"
                    checked={selectedIds.includes(item.Cart_ID)}
                    onChange={() => handleSelectItem(item.Cart_ID)}
                />
                <Link to={`/product-detail/${item.Style_ID}`} style={{ display: 'block', height: '100%' }}>
                    <img
                        src={item.Image1 || DEFAULT_IMAGE}
                        alt={item.StyleNo}
                        onError={(e) => { e.currentTarget.src = DEFAULT_IMAGE; }}
                    />
                </Link>
            </div>

            <div className="item-details-container">
                <div className="item-info-col">
                    <h2 className="item-title-exact">{item.Line1 || "Untitled Design"}</h2>
                    <div className="item-ref-code">
                        Jewel Code: <span>{item.JewelCode}</span>
                    </div>
                    <div className="item-ref-code">
                        Style No: <span>{item.StyleNo}</span>
                    </div>
                    <div className="item-clock-date">
                        <i className="pi pi-clock" style={{ fontSize: '0.9rem' }} />
                        {formatDate(item.InsertDatetime || item.CartEntryDate)}
                    </div>
                </div>

                <div className="spec-column">
                    <div className="spec-title-exact">Product Details</div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Purity</span>
                        <span className="spec-value-exact">{item.MetalQuality || "-"}</span>
                    </div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Gold Wt</span>
                        <span className="spec-value-exact">{item.TotalNetWt ? `${item.TotalNetWt} g` : "-"}</span>
                    </div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Dimension</span>
                        <span className="spec-value-exact">{item.ItemSize || "-"}</span>
                    </div>
                </div>

                <div className="spec-column">
                    <div className="spec-title-exact">
                        {item.CDiaWt ? "Stone Specs" : "Diamond Details"}
                    </div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Weight</span>
                        <span className="spec-value-exact">
                            {item.DiaPcs || 0} Pcs / {item.DiaWt || 0} Cts
                        </span>
                    </div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Gross Wt</span>
                        <span className="spec-value-exact">{item.GrossWt ? `${item.GrossWt} g` : "-"}</span>
                    </div>
                    <div className="spec-row-exact">
                        <span className="spec-label-exact">Color</span>
                        <span className="spec-value-exact">{item.MetalTone || "-"}</span>
                    </div>
                </div>

                <div className="item-actions-col">
                    <div className="item-price-exact fs-4">
                        {item.LineAmount || item.TotalAmount}
                    </div>
                    <div className="item-action-btns-wrapper" style={{ marginTop: 'auto' }}>
                        <button
                            className="delete-btn-exact"
                            onClick={() => openDeleteModal(item)}
                            title="Remove Item"
                        >
                            <i className="pi pi-trash" />
                        </button>
                    </div>
                </div>
            </div>
        </motion.div>
    );

    return (
        <div className="live-cart-page">
            <div className="live-cart-header-card">
                <div className="live-cart-header-top-row">
                    <h1 className="live-cart-page-title">
                        Cart <span>({cartItems.length} items)</span>
                    </h1>
                    <div className="live-cart-header-actions-right">
                        <div className="header-select-all">
                            <span>SELECT ALL ({selectedIds.length.toString().padStart(1, '0')})</span>
                            {selectedIds.length > 1 && (
                                <button 
                                    className="header-delete-selected-btn" 
                                    onClick={openBulkDeleteModal}
                                    title="Remove Selected Items"
                                >
                                    <i className="pi pi-trash" />
                                </button>
                            )}
                            <input
                                type="checkbox"
                                className="item-img-checkbox"
                                checked={cartItems.length > 0 && selectedIds.length === cartItems.length}
                                onChange={handleSelectAll}
                                style={{ position: 'static' }}
                            />
                        </div>
                        <button
                            className="btn-primary"
                            disabled={selectedIds.length === 0}
                            onClick={handleProceedToEnquiry}
                        >
                            Enquire Now
                        </button>
                        <div className="header-actions-group">
                            <div className="cart-header-actions-left">
                                <CustomerSelection />
                            </div>
                            <button className="back-btn" onClick={() => window.history.back()} aria-label="Go back">
                                <i className="pi pi-arrow-left"></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {cartItems.length === 0 && !isLoading ? (
                <div className="empty-cart-premium">
                    <div className="empty-cart-visual">
                        <i className="pi pi-shopping-cart" />
                    </div>
                    <div className="empty-cart-content">
                        <h2 className="empty-cart-title">Your Cart is Empty</h2>
                        <p className="empty-cart-text">
                            Looks like you haven't added anything to your cart yet.
                            Explore our collections to find your perfect match.
                        </p>
                        <Link to="/StyleInventory" className="btn-primary">
                            <i className="pi pi-compass me-2" />
                            Browse Collections
                        </Link>
                    </div>
                </div>
            ) : (
                (Number(user?.SelectedCustomer_ID || 0) === 0) ? (
                    <div className="accordion-full-width">
                        <AnimatePresence>
                            {isLoading ? (
                                <div className="skeleton-list">
                                    {Array.from({ length: 3 }).map((_, i) => (
                                        <div className="cart-item-exact skeleton-item" key={i}>
                                            <Skeleton width="100%" height="160px" borderRadius="12px" />
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <Accordion multiple={true} className="customer-accordion">
                                    {Object.entries(groupedData).map(([customerName, items]) => (
                                        <AccordionTab
                                            key={customerName}
                                            header={
                                                <div className="accordion-header-content">
                                                    <span>{customerName}</span>
                                                    <span className="customer-badge">{items.length} items</span>
                                                </div>
                                            }
                                        >
                                            <div className="customer-items-list">
                                                {items.map((item, index) => renderCartItem(item, index))}
                                            </div>
                                        </AccordionTab>
                                    ))}
                                </Accordion>
                            )}
                        </AnimatePresence>
                    </div>
                ) : (
                    <div className="live-cart-layout">
                        <div className="cart-main-section">
                            <div className="cart-items-list-exact">
                                <AnimatePresence>
                                    {isLoading ? (
                                        <div className="skeleton-list">
                                            {Array.from({ length: 3 }).map((_, i) => (
                                                <div className="cart-item-exact skeleton-item" key={i}>
                                                    <Skeleton width="100%" height="160px" borderRadius="12px" />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="customer-items-list">
                                            {cartItems.map((item, index) => renderCartItem(item, index))}
                                        </div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                    </div>
                )
            )}

            <Dialog
                visible={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                footer={deleteModalFooter}
                draggable={false}
                resizable={false}
                showHeader={false}
                maskClassName="cart-modal-mask"
                className="cart-premium-modal"
                dismissableMask
            >
                <div className="cart-modal-content-wrapper">
                    <button className="modal-close-x" onClick={() => setShowDeleteModal(false)}>
                        <i className="pi pi-times" />
                    </button>
                    
                    <div className="cart-modal-header-visual">
                        <div className="cart-modal-icon-bg">
                            <i className="pi pi-shopping-bag" />
                        </div>
                        <h2 className="cart-modal-title">Remove Item?</h2>
                        <p className="cart-modal-subtitle">This item will be removed from your shopping cart.</p>
                    </div>

                    {itemToDelete && (
                        <div className="cart-modal-product-preview">
                            <div className="preview-image-container">
                                <img 
                                    src={itemToDelete.Image1 || DEFAULT_IMAGE} 
                                    alt={itemToDelete.StyleNo}
                                    onError={(e) => { e.currentTarget.src = DEFAULT_IMAGE; }}
                                />
                            </div>
                            <div className="preview-details">
                                <div className="preview-name">{itemToDelete.Line1 || "Untitled Design"}</div>
                                <div className="preview-style">Style: {itemToDelete.StyleNo}</div>
                                <div className="preview-code">Code: {itemToDelete.JewelCode}</div>
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>

            <Dialog
                visible={showBulkDeleteModal}
                onHide={() => setShowBulkDeleteModal(false)}
                footer={bulkDeleteModalFooter}
                draggable={false}
                resizable={false}
                showHeader={false}
                maskClassName="cart-modal-mask"
                className="cart-premium-modal"
                dismissableMask
            >
                <div className="cart-modal-content-wrapper">
                    <button className="modal-close-x" onClick={() => setShowBulkDeleteModal(false)}>
                        <i className="pi pi-times" />
                    </button>
                    
                    <div className="cart-modal-header-visual">
                        <div className="cart-modal-icon-bg" style={{ background: '#fef2f2', color: '#dc2626' }}>
                            <i className="pi pi-trash" />
                        </div>
                        <h2 className="cart-modal-title">Remove Selected?</h2>
                        <p className="cart-modal-subtitle">Are you sure you want to remove <b>{selectedIds.length} items</b> from your cart?</p>
                    </div>

                    <div className="bulk-selection-preview-list">
                        {cartItems.filter(item => selectedIds.includes(item.Cart_ID)).slice(0, 3).map((item, i) => (
                            <div key={i} className="cart-modal-product-preview mini">
                                <div className="preview-image-container mini">
                                    <img src={item.Image1 || DEFAULT_IMAGE} alt={item.StyleNo} />
                                </div>
                                <div className="preview-details">
                                    <div className="preview-name mini">{item.StyleNo}</div>
                                </div>
                            </div>
                        ))}
                        {selectedIds.length > 3 && (
                            <div className="more-items-label">+ {selectedIds.length - 3} more items</div>
                        )}
                    </div>
                </div>
            </Dialog>

            <CommonPopUpModal
                isOpen={isSuccessModalOpen}
                onClose={() => setIsSuccessModalOpen(false)}
                message={successMessage}
                pageType={successType || "error"}
                title={
                    successType === "enquiry" ? (
                        <>
                            <div className="popup-subtitle">
                                Thanks for reaching out to us.
                            </div>
                            <div className="popup-enquiry-label">
                                Your Enquiry No is
                            </div>
                            <div className="popup-enquiry-no">
                                <span>{successTitle}</span>
                            </div>
                        </>
                    ) : (
                        successTitle
                    )
                }
            />

            <EnquiryModal
                isOpen={isEnquiryOpen}
                toggle={resetEnquiryState}
                items={enquiryItems}
                remark={enquiryRemark}
                setRemark={setEnquiryRemark}
                onRemoveItem={removeEnquiryItem}
                onSubmit={submitEnquiry}
                submitting={submittingEnquiry}
                title="Cart Enquiry"
            />
        </div>
    );
}
